import simplify from '@turf/simplify'
import DrawModeBase from '@/lib/mapbox/draw-mode-base'
import pubsub from '@/lib/pubsub'
import { getToleranceDegrees } from '@/lib/mapbox/draw-mode-helpers'
import { setCursor, resetCursor } from '@/services/map-service'
import { MARKUP_COORDINATES_CHANGED } from '@/lib/mapbox/drawing-events'

/**
 * Allows the user to draw a new shape by clicking and free-hand drawing the
 * vertices.
 */
class FreehandDrawShapeMode extends DrawModeBase {
  /**
   * @inheritDoc
   */
  onSetup(geoJson) {
    const id = geoJson.properties.id
    geoJson.geometry.coordinates = [[]]
    setCursor('crosshair')

    this._state = {
      id,
      isDrawing: false,
      spacebarPressed: false,
      handleUndo: this._handleUndo.bind(this),
      handleSpacebar: this._handleSpacebar.bind(this),
      undoQueue: [],
      freeDrawStartVertexIndex: null,
    }

    pubsub.subscribe('markupUndo', this._state.handleUndo)

    document.addEventListener('keydown', this._state.handleSpacebar)
    document.addEventListener('keyup', this._state.handleSpacebar)
  }

  /**
   * Handle the keydown and keyup events for pressing the spacebar
   * @param {Object} evt - listener event
   */
  _handleSpacebar(evt) {
    if (evt.keyCode === 32) {
      if (evt.type === 'keydown') {
        this._state.spacebarPressed = true
      } else if (evt.type === 'keyup') {
        this._state.spacebarPressed = false
      }
    }
  }

  /**
   * @inheritdoc
   */
  onTearDown() {
    resetCursor()
    if (this._state.handleUndo) {
      pubsub.unsubscribe('markupUndo', this._state.handleUndo)
    }
    document.removeEventListener('keydown', this._state.handleSpacebar)
    document.removeEventListener('keyup', this._state.handleSpacebar)
  }

  /**
   * @inheritDoc
   */
  onMouseDown(event) {
    // allow user to pan the map when the spacebar is pressed
    if (this._state.spacebarPressed) {
      return
    }

    const cursorCoord = [event.lngLat.lng, event.lngLat.lat]
    const coordinates = this._getCoordinates()
    this._state.freeDrawStartVertexIndex =
      coordinates?.length === 0 ? 0 : coordinates?.length - 1
    this._startDrawing()
    this._addVertex(cursorCoord)
  }

  /**
   * @inheritDoc
   */
  onMouseMove(event) {
    const isDrawing = this._state.isDrawing

    if (isDrawing) {
      const cursorCoord = [event.lngLat.lng, event.lngLat.lat]
      this._addVertex(cursorCoord)
    }
  }

  /**
   * @inheritDoc
   */
  onMouseUp() {
    // if spacebar was pressed, user was panning the map
    if (this._state.spacebarPressed) {
      return
    }

    this._endDrawing()
  }

  /**
   * @inheritDoc
   */
  toDisplayFeatures(geoJson) {
    return [geoJson]
  }

  /**
   * Starts drawing the shape.
   * @private
   */
  _startDrawing() {
    this._state.isDrawing = true
    this.map.dragPan.disable()
  }

  /**
   * Ends drawing the shape.
   * @private
   */
  _endDrawing() {
    const id = this._state.id
    const feature = this.getFeature(id)
    const tolerance = getToleranceDegrees(2)
    // only attempt to simplify if there is a valid polygon geometry
    if (feature.geometry.coordinates.length > 2) {
      simplify(feature.geometry, { tolerance, mutate: true })
    }
    this._state.isDrawing = false
    this.map.dragPan.enable()
    this.events.emit(MARKUP_COORDINATES_CHANGED, {
      id,
      coordinates: feature.geometry.coordinates,
    })
    this._state.undoQueue.push({
      type: 'free_draw',
      index: this._state.freeDrawStartVertexIndex,
    })
  }

  /**
   * Adds a vertex to the shape feature at the given coordinate.
   * @param {Array<number>} coord
   * @private
   */
  _addVertex(coord) {
    const id = this._state.id
    const feature = this.getFeature(id)
    const coordinates = feature.geometry.coordinates[0]
    const isFirstVertex = coordinates.length === 0
    const vertexAddPosition = coordinates.length - 1

    if (isFirstVertex) {
      // two vertices so that shape loops back on itself
      coordinates.push(coord)
      coordinates.push(coord)
    } else {
      // new vertices are inserted just behind last vertex to preserve shape
      // loop
      coordinates.splice(vertexAddPosition, 0, coord)
    }

    this.updated()
  }

  /**
   * Handles the undo button clicked.
   */
  _handleUndo() {
    const command = this._state.undoQueue.pop()
    if (!command) {
      return
    }

    this._undoFreeDraw(command)
  }

  /**
   * Undo free draw command
   * @param {object} command - Free draw command
   */
  _undoFreeDraw(command) {
    const coordinates = this._getCoordinates()
    if (command.index === 0) {
      coordinates.splice(0, coordinates.length)
    } else {
      coordinates.splice(command.index - 1, coordinates.length - command.index)
    }

    this.updated()
    this.events.emit(MARKUP_COORDINATES_CHANGED, {
      id: this._state.id,
      coordinates: [coordinates],
    })
  }

  /**
   * Gets the shape coordinates
   * @return {array} coordinates
   */
  _getCoordinates() {
    const feature = this._getFeature()
    return feature.geometry.coordinates[0]
  }

  /**
   * Returns the shape feature
   * @return {object} Shape feature
   */
  _getFeature() {
    const id = this._state.id
    return this.getFeature(id)
  }
}

export default FreehandDrawShapeMode
